import { useMemo } from "react";
import clsx from "clsx";
import { polygonCentroid, polygonToRoundedPath } from "@/lib/sitePlan";
import type { GuestTabletSitePlan } from "@/types/guestTablet";

type Props = {
  sitePlan: GuestTabletSitePlan;
  onPitchSelect?: (pitchId: string) => void;
  selecting?: boolean;
};

export function TabletPitchPlan({ sitePlan, onPitchSelect, selecting = false }: Props) {
  const badgeScale = Number(sitePlan.badge_scale) || 1;

  const highlightByPitch = useMemo(() => {
    const map = new Map<string, string>();
    for (const pitch of sitePlan.pitches) {
      map.set(pitch.pitch_id, pitch.highlight);
    }
    return map;
  }, [sitePlan.pitches]);

  if (!sitePlan.image_data) {
    return (
      <p className="text-center text-lg text-emerald-900/70 dark:text-emerald-50/70">
        Platzplan ist noch nicht hinterlegt.
      </p>
    );
  }

  return (
    <div className="relative w-full overflow-hidden rounded-2xl border border-emerald-900/15 bg-white/50 dark:border-white/10 dark:bg-black/20">
      <img
        src={sitePlan.image_data}
        alt="Platzplan"
        className="block h-auto w-full max-w-full"
        draggable={false}
      />
      <svg viewBox="0 0 1 1" preserveAspectRatio="none" className="absolute inset-0 h-full w-full">
        {sitePlan.pitches.map((pitch) => {
          if (!pitch.map_polygon || pitch.map_polygon.length < 3) return null;
          const highlight = highlightByPitch.get(pitch.pitch_id) ?? "dimmed";
          if (selecting && highlight !== "candidate") return null;
          const hoverable = selecting;
          const interactive = selecting && highlight === "candidate";
          const surfaceClass =
            highlight === "assigned"
              ? "site-plan-pitch--accent site-plan-pitch--selected"
              : highlight === "candidate"
                ? "site-plan-pitch--amber site-plan-pitch--candidate"
                : "site-plan-pitch--neutral site-plan-pitch--dimmed-polygon";
          return (
            <g
              key={pitch.pitch_id}
              className={clsx(
                hoverable && "site-plan-pitch--hoverable",
                interactive && "site-plan-pitch--interactive",
              )}
            >
              <path
                d={polygonToRoundedPath(pitch.map_polygon)}
                className={clsx("site-plan-pitch", "site-plan-pitch__backing", surfaceClass)}
                aria-hidden
              />
              <path
                d={polygonToRoundedPath(pitch.map_polygon)}
                className={clsx(
                  "site-plan-pitch",
                  "site-plan-pitch__surface",
                  surfaceClass,
                )}
                role={interactive ? "button" : undefined}
                tabIndex={interactive ? 0 : undefined}
                onClick={() => {
                  if (interactive && onPitchSelect) onPitchSelect(pitch.pitch_id);
                }}
                onKeyDown={(e) => {
                  if (!interactive || !onPitchSelect) return;
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onPitchSelect(pitch.pitch_id);
                  }
                }}
              />
            </g>
          );
        })}
      </svg>
      {sitePlan.pitches.map((pitch) => {
        if (!pitch.map_polygon || pitch.map_polygon.length < 3) return null;
        const center = polygonCentroid(pitch.map_polygon);
        const highlight = highlightByPitch.get(pitch.pitch_id) ?? "dimmed";
        if (selecting && highlight !== "candidate") return null;
        if (highlight === "dimmed") return null;
        return (
          <span
            key={`label-${pitch.pitch_id}`}
            className="pointer-events-none absolute -translate-x-1/2 -translate-y-1/2 rounded-md bg-white/90 px-2 py-0.5 text-sm font-bold text-emerald-950 shadow dark:bg-emerald-950/90 dark:text-emerald-50"
            style={{ left: `${center.x * 100}%`, top: `${center.y * 100}%`, fontSize: `${0.75 * badgeScale}rem` }}
          >
            {pitch.code}
          </span>
        );
      })}
    </div>
  );
}
